import {
  SupplierOrderDto,
  SupplierOrderManualLandedCostCategory,
} from '../../dto/supplierOrder';
import { SupplierOrderItemPayload, SupplierOrderPayload } from './api/supplierOrdersApi';
import type { Dayjs } from 'dayjs';
import { parseDate, toISOString } from '../../utils/dateUtils';

export type SupplierOrderEditableField =
  | 'supplierId'
  | 'factoryId'
  | 'status'
  | 'currency'
  | 'landedCosts'
  | 'accountManagerId'
  | 'manualNumbering'
  | 'clientOrderNumber'
  | 'producer'
  | 'factoryLocation'
  | 'deliveryLocation'
  | 'description'
  | 'orderDate'
  | 'expectedDelivery'
  | 'actualDelivery'
  | 'estimatedCompletionDate'
  | 'supplierReference'
  | 'proforma'
  | 'invoice'
  | 'waybill'
  | 'notes';

interface SupplierOrderLandedCostFormValue {
  category?: SupplierOrderManualLandedCostCategory;
  amount?: number;
  currency?: string;
  note?: string | null;
}

export const getSupplierOrderInitialValues = (
  order: SupplierOrderDto,
  field: SupplierOrderEditableField
): Record<string, unknown> => {
  const initialValues: Record<string, unknown> = {};

  if (field === 'supplierId') initialValues.supplierId = order.supplierId || undefined;
  if (field === 'factoryId') initialValues.factoryId = order.factoryId || undefined;
  if (field === 'status') initialValues.status = order.status || 'DRAFT';
  if (field === 'currency') initialValues.currency = order.currency || undefined;
  if (field === 'landedCosts') {
    // Edit in the line's own currency: originalAmount/originalCurrency are the
    // entered values (amount is a legacy mirror of originalAmount). Historical
    // TRANSPORT rows are deliberately excluded: the backend preserves them as
    // read-only records while new freight costs live in the Transport workflow.
    initialValues.landedCosts = (order.landedCosts || [])
      .filter((item) => item.category !== 'TRANSPORT')
      .map((item) => ({
        category: item.category,
        amount: item.originalAmount ?? item.amount,
        currency: item.originalCurrency || order.currency || 'PLN',
        note: item.note || '',
      }));
  }
  if (field === 'accountManagerId') initialValues.accountManagerId = order.accountManagerId || undefined;
  if (field === 'manualNumbering') {
    initialValues.useManualNumbering = order.useManualNumbering;
    initialValues.manualOrderNumber = order.manualOrderNumber || '';
  }
  if (field === 'clientOrderNumber') initialValues.clientOrderNumber = order.clientOrderNumber || '';
  if (field === 'producer') initialValues.producer = order.producer || '';
  if (field === 'factoryLocation') initialValues.factoryLocation = order.factoryLocation || '';
  if (field === 'deliveryLocation') initialValues.deliveryLocation = order.deliveryLocation || '';
  if (field === 'description') initialValues.description = order.description || '';
  if (field === 'orderDate') initialValues.orderDate = parseDate(order.orderDate);
  if (field === 'expectedDelivery') initialValues.expectedDelivery = parseDate(order.expectedDelivery);
  if (field === 'actualDelivery') initialValues.actualDelivery = parseDate(order.actualDelivery);
  if (field === 'estimatedCompletionDate') initialValues.estimatedCompletionDate = parseDate(order.estimatedCompletionDate);
  if (field === 'supplierReference') initialValues.supplierReference = order.supplierReference || '';
  if (field === 'proforma') {
    initialValues.proformaNumber = order.proformaNumber || '';
    initialValues.proformaDate = parseDate(order.proformaDate);
  }
  if (field === 'invoice') {
    initialValues.invoiceNumber = order.invoiceNumber || '';
    initialValues.invoiceDate = parseDate(order.invoiceDate);
  }
  if (field === 'waybill') {
    initialValues.waybillNumber = order.waybillNumber || '';
    initialValues.waybillDate = parseDate(order.waybillDate);
  }
  if (field === 'notes') initialValues.notes = order.notes || '';

  return initialValues;
};

export const getSupplierOrderFieldNames = (
  field: SupplierOrderEditableField,
  values: Record<string, any> = {}
): string[] => {
  if (field === 'manualNumbering') {
    return values.useManualNumbering ? ['useManualNumbering', 'manualOrderNumber'] : ['useManualNumbering'];
  }
  if (field === 'proforma') return ['proformaNumber', 'proformaDate'];
  if (field === 'invoice') return ['invoiceNumber', 'invoiceDate'];
  if (field === 'waybill') return ['waybillNumber', 'waybillDate'];

  if (field === 'landedCosts') {
    return ['landedCosts'];
  }

  const singleFieldMap: Record<Exclude<SupplierOrderEditableField, 'manualNumbering' | 'proforma' | 'invoice' | 'waybill' | 'landedCosts'>, string> = {
    supplierId: 'supplierId',
    factoryId: 'factoryId',
    status: 'status',
    currency: 'currency',
    accountManagerId: 'accountManagerId',
    clientOrderNumber: 'clientOrderNumber',
    producer: 'producer',
    factoryLocation: 'factoryLocation',
    deliveryLocation: 'deliveryLocation',
    description: 'description',
    orderDate: 'orderDate',
    expectedDelivery: 'expectedDelivery',
    actualDelivery: 'actualDelivery',
    estimatedCompletionDate: 'estimatedCompletionDate',
    supplierReference: 'supplierReference',
    notes: 'notes',
  };

  return [singleFieldMap[field]];
};

// The backend route requires `supplierId` on every PUT (express-validator
// body('supplierId').notEmpty()), and the service merges any omitted header
// field from the current order while only replacing items when `items` is
// present. So a per-field save sends supplierId + the changed field and leaves
// items and every other field untouched.
export const buildSupplierOrderUpdateData = (
  order: SupplierOrderDto,
  field: SupplierOrderEditableField,
  values: Record<string, any>
): SupplierOrderPayload => {
  const updateData: SupplierOrderPayload = { supplierId: order.supplierId };

  if (field === 'supplierId') updateData.supplierId = values.supplierId;
  if (field === 'factoryId') updateData.factoryId = values.factoryId || null;
  if (field === 'status') updateData.status = values.status;
  if (field === 'currency') updateData.currency = values.currency;
  if (field === 'landedCosts') {
    updateData.landedCosts = (values.landedCosts || []).map((item: SupplierOrderLandedCostFormValue) => ({
      category: item.category || 'OTHER',
      amount: Number(item.amount ?? 0),
      currency: String(item.currency || 'PLN'),
      note: typeof item.note === 'string' && item.note.trim() ? item.note.trim() : null,
    }));
    updateData.landedCostAllocationMethod = 'QUANTITY';
  }
  if (field === 'accountManagerId') updateData.accountManagerId = values.accountManagerId || null;
  if (field === 'manualNumbering') {
    updateData.useManualNumbering = Boolean(values.useManualNumbering);
    updateData.manualOrderNumber = values.useManualNumbering
      ? values.manualOrderNumber?.trim() || null
      : null;
  }
  if (field === 'clientOrderNumber') updateData.clientOrderNumber = values.clientOrderNumber?.trim() || null;
  if (field === 'producer') updateData.producer = values.producer?.trim() || null;
  if (field === 'factoryLocation') updateData.factoryLocation = values.factoryLocation?.trim() || null;
  if (field === 'deliveryLocation') updateData.deliveryLocation = values.deliveryLocation?.trim() || null;
  if (field === 'description') updateData.description = values.description?.trim() || null;
  if (field === 'orderDate') updateData.orderDate = toISOString(values.orderDate) || undefined;
  if (field === 'expectedDelivery') updateData.expectedDelivery = toISOString(values.expectedDelivery);
  if (field === 'actualDelivery') updateData.actualDelivery = toISOString(values.actualDelivery);
  if (field === 'estimatedCompletionDate') updateData.estimatedCompletionDate = toISOString(values.estimatedCompletionDate);
  if (field === 'supplierReference') updateData.supplierReference = values.supplierReference?.trim() || null;
  if (field === 'proforma') {
    updateData.proformaNumber = values.proformaNumber?.trim() || null;
    updateData.proformaDate = toISOString(values.proformaDate);
  }
  if (field === 'invoice') {
    updateData.invoiceNumber = values.invoiceNumber?.trim() || null;
    updateData.invoiceDate = toISOString(values.invoiceDate);
  }
  if (field === 'waybill') {
    updateData.waybillNumber = values.waybillNumber?.trim() || null;
    updateData.waybillDate = toISOString(values.waybillDate);
  }
  if (field === 'notes') updateData.notes = values.notes?.trim() || null;

  return updateData;
};

// Items save shares the same "always include supplierId" invariant as the
// per-field save above, so it lives here too rather than being re-derived at
// the call site. Sending `items` makes the backend replace the line items.
export const buildSupplierOrderItemsUpdateData = (
  order: SupplierOrderDto,
  items: SupplierOrderItemPayload[]
): SupplierOrderPayload => ({
  supplierId: order.supplierId,
  items,
});

/**
 * „Więcej danych” of the ZD card in stage „Szkic” (karta ZD według makiety,
 * 2026-10-05): the fields of the former ZD wizard that the mockup does not
 * show — factory, order date, numbering, planned ready date, supplier
 * reference, proforma number and notes. The same values feed the first save of
 * a new ZD (supplierOrderNewDraft.ts) and the update of a saved draft.
 */
export interface SupplierOrderMoreDataValues {
  factoryId?: string | null;
  orderDate?: Dayjs | null;
  useManualNumbering?: boolean;
  manualOrderNumber?: string | null;
  expectedDelivery?: Dayjs | null;
  supplierReference?: string | null;
  proformaNumber?: string | null;
  notes?: string | null;
}

export const getSupplierOrderMoreDataInitialValues = (
  order: Pick<SupplierOrderDto, 'factoryId' | 'orderDate' | 'useManualNumbering' | 'manualOrderNumber'
    | 'expectedDelivery' | 'supplierReference' | 'proformaNumber' | 'notes'>,
): SupplierOrderMoreDataValues => ({
  factoryId: order.factoryId || undefined,
  orderDate: parseDate(order.orderDate),
  useManualNumbering: Boolean(order.useManualNumbering),
  manualOrderNumber: order.manualOrderNumber || '',
  expectedDelivery: parseDate(order.expectedDelivery),
  supplierReference: order.supplierReference || '',
  proformaNumber: order.proformaNumber || '',
  notes: order.notes || '',
});

const trimmedOrNull = (value?: string | null): string | null => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

/** Header fields of „Więcej danych” as a ZD payload fragment (no supplier, no items). */
export const buildSupplierOrderMoreDataFields = (
  values: SupplierOrderMoreDataValues,
): Omit<SupplierOrderPayload, 'supplierId'> => {
  const orderDate = toISOString(values.orderDate ?? null);
  return {
    factoryId: values.factoryId || null,
    ...(orderDate ? { orderDate } : {}),
    useManualNumbering: Boolean(values.useManualNumbering),
    manualOrderNumber: values.useManualNumbering ? trimmedOrNull(values.manualOrderNumber) : null,
    expectedDelivery: toISOString(values.expectedDelivery ?? null),
    supplierReference: trimmedOrNull(values.supplierReference),
    proformaNumber: trimmedOrNull(values.proformaNumber),
    notes: trimmedOrNull(values.notes),
  };
};

/** PUT payload of „Zapisz dane” in „Więcej danych” of a saved draft. */
export const buildSupplierOrderMoreDataUpdateData = (
  order: Pick<SupplierOrderDto, 'supplierId'>,
  values: SupplierOrderMoreDataValues,
): SupplierOrderPayload => ({
  supplierId: order.supplierId,
  ...buildSupplierOrderMoreDataFields(values),
});
